import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatGBP, formatUKDate, todayISO } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Plus, Check, X, RotateCw, CircleDollarSign } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function Jobs() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ customer_id: "", scheduled_date: todayISO(), price: "", assigned_to: "", notes: "" });
  const [statusFilter, setStatusFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const [jobs, custs, st] = await Promise.all([
        api.get("/jobs", { params }),
        api.get("/customers"),
        api.get("/staff"),
      ]);
      setItems(jobs.data);
      setCustomers(custs.data);
      setStaff(st.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/jobs", {
        ...form,
        price: form.price === "" ? null : Number(form.price),
        assigned_to: form.assigned_to || null,
      });
      toast.success("Job created");
      setShowNew(false);
      setForm({ customer_id: "", scheduled_date: todayISO(), price: "", assigned_to: "", notes: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create");
    }
  };

  const complete = async (id) => {
    await api.post(`/jobs/${id}/complete`);
    toast.success("Job marked complete. Invoice sent.");
    load();
  };
  const skip = async (id) => { await api.post(`/jobs/${id}/skip`); load(); };
  const noAccess = async (id) => { await api.post(`/jobs/${id}/no-access`); load(); };
  const cash = async (id) => { await api.post(`/jobs/${id}/mark-cash`); toast.success("Marked paid in cash"); load(); };
  const reschedule = async (id) => {
    const date = window.prompt("Reschedule to (YYYY-MM-DD):", todayISO());
    if (!date) return;
    await api.post(`/jobs/${id}/reschedule`, { scheduled_date: date });
    toast.success("Rescheduled");
    load();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8" data-testid="jobs-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-3xl tracking-tight font-bold">Jobs</h1>
        <div className="flex items-center gap-2">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-md border border-input px-3 py-2 text-sm bg-transparent" data-testid="jobs-filter-status">
            <option value="">All statuses</option>
            <option value="scheduled">Scheduled</option>
            <option value="completed">Completed</option>
            <option value="skipped">Skipped</option>
            <option value="no_access">No Access</option>
          </select>
          <button onClick={() => setShowNew(true)} className="inline-flex items-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90" data-testid="new-job-button">
            <Plus className="h-4 w-4" /> New job
          </button>
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-accent text-left text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Postcode</th>
                <th className="px-4 py-3">Price</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Payment</th>
                <th className="px-4 py-3">Staff</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">No jobs yet.</td></tr>
              ) : items.map((j) => {
                const staffName = staff.find((s) => s.id === j.assigned_to);
                return (
                  <tr key={j.id} className="hover:bg-muted/40" data-testid={`job-row-${j.id}`}>
                    <td className="px-4 py-3 whitespace-nowrap">{formatUKDate(j.scheduled_date)}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{j.customer_name}</div>
                      <div className="text-xs text-muted-foreground truncate max-w-[240px]">{j.customer_address}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{j.customer_postcode}</td>
                    <td className="px-4 py-3">{formatGBP(j.price)}</td>
                    <td className="px-4 py-3"><StatusBadge status={j.status} /></td>
                    <td className="px-4 py-3"><StatusBadge status={j.payment_status} /></td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{staffName ? `${staffName.first_name} ${staffName.last_name[0]}.` : "—"}</td>
                    <td className="px-4 py-3 text-right space-x-1 whitespace-nowrap">
                      {j.status === "scheduled" && (
                        <>
                          <button onClick={() => complete(j.id)} title="Complete" className="p-1.5 rounded hover:bg-emerald-50 text-emerald-700" data-testid={`complete-job-${j.id}`}><Check className="h-4 w-4" /></button>
                          <button onClick={() => noAccess(j.id)} title="No access" className="p-1.5 rounded hover:bg-rose-50 text-rose-700" data-testid={`no-access-${j.id}`}><X className="h-4 w-4" /></button>
                          <button onClick={() => reschedule(j.id)} title="Reschedule" className="p-1.5 rounded hover:bg-amber-50 text-amber-700" data-testid={`reschedule-${j.id}`}><RotateCw className="h-4 w-4" /></button>
                          <button onClick={() => skip(j.id)} title="Skip" className="text-xs px-2 py-1 rounded hover:bg-muted" data-testid={`skip-${j.id}`}>Skip</button>
                        </>
                      )}
                      {j.payment_status === "invoice_sent" && (
                        <button onClick={() => cash(j.id)} title="Mark paid cash" className="p-1.5 rounded hover:bg-emerald-50 text-emerald-700" data-testid={`cash-${j.id}`}><CircleDollarSign className="h-4 w-4" /></button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>New job</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-4" data-testid="new-job-form">
            <div>
              <label className="text-sm font-medium">Customer</label>
              <select required value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} className="mt-1 w-full rounded-md border border-input px-3 py-2 text-sm bg-transparent" data-testid="job-customer">
                <option value="">Select…</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name} — {c.postcode}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Date</label>
              <input type="date" required value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} className="mt-1 w-full rounded-md border border-input px-3 py-2 text-sm bg-transparent" data-testid="job-date" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Custom price £ (optional)</label>
                <input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1 w-full rounded-md border border-input px-3 py-2 text-sm bg-transparent" data-testid="job-price" />
              </div>
              <div>
                <label className="text-sm font-medium">Assign to</label>
                <select value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} className="mt-1 w-full rounded-md border border-input px-3 py-2 text-sm bg-transparent" data-testid="job-staff">
                  <option value="">— Unassigned —</option>
                  {staff.map((s) => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Notes</label>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1 w-full rounded-md border border-input px-3 py-2 text-sm bg-transparent" data-testid="job-notes" />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowNew(false)} className="rounded-md border border-border px-4 py-2 text-sm">Cancel</button>
              <button type="submit" className="rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium" data-testid="job-submit">Create</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
